import { Box, Button, Typography } from "@mui/material";
import * as moment from "moment";
import { QRCodeSVG } from "qrcode.react";
import React from "react";
import { connect, useSelector } from "react-redux";
import ReactToPrint from "react-to-print";

import { convertADtoBS } from "../../../../components/Calendar/functions/calendarFunctions";
import { separateByComma } from "../../../../helpers/formatters";
import useGetDepartments from "../../../../hooks/department";
import usePrintStyles from "../../../../hooks/usePrintStyles";
import { ServiceProvider } from "../../../../interfaces/ServiceProvidersInterface";
import { clientFullNameSelector } from "../../../../reducers/client";
import { RootState } from "../../../../store";
import { getCustomerNumber } from "../../ClientList";
import { Client } from "../../../../interfaces/ClientInterface";
import { ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";
import { useClientQueueNumber } from "../../../../hooks/useClientQueueNumber";
import { getClientAge, getClientGender, getQRUrl, getSpFullName } from "../StickerUtils";
import { rupeesDisplayFull } from "../../../../helpers/util";
import { useShowClientAgeInYearsOnly } from "../../../../hooks/useShowClientAgeInYearsOnly";

export const PrintRow = ({
  label,
  value,
  emphasizeValue = false
}: {
  label: string;
  value: string;
  emphasizeValue?: boolean;
}): React.ReactNode => (
  <Box sx={{ display: "flex" }}>
    <Typography sx={{ fontSize: "1.8mm", fontFamily: "Verdana", width: "15mm", flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "1.95mm",
        fontFamily: "Verdana",
        ...(emphasizeValue ? { fontWeight: 700 } : {})
      }}
    >
      {value}
    </Typography>
  </Box>
);
const ClientStickerDefaultTemplate = ({
  client,
  resourceCentre,
  rcLogo,
  onPrintClick,
  trigger,
  serviceProviderId
}: {
  client: Client;
  serviceProviderId?: number;

  trigger?: React.ReactElement;
  onPrintClick?: () => void;

  // comes from connect
  resourceCentre?: ResourceCentre;
  rcLogo?: string;
}) => {
  const printRef = React.useRef(null);

  // custom hooks
  const { showClientAgeInYearsOnly } = useShowClientAgeInYearsOnly();
  const departments = useGetDepartments({ showDeletedDepartments: true });
  const { queueNumber, refetch } = useClientQueueNumber({ serviceProviderId });

  usePrintStyles({
    pageStyle: "size: 3.375in 1.75in; margin: 2mm;"
  }).applyPrintStyles();

  const serviceProviders: ServiceProvider[] = useSelector(
    (state: RootState) => state.resources.resourceCentreServiceProviders
  );

  const currentDateAndTime = moment().format();
  const currentTime = currentDateAndTime.slice(11, 16);
  const fullName = clientFullNameSelector(client).toUpperCase();
  const BSDate = convertADtoBS(moment.utc(currentDateAndTime.slice(0, 10))).formatted;

  const spIdToUse = serviceProviderId ?? client?.lastHandledSpId;
  const serviceProvider = serviceProviders.find((sp) => Number(sp.id) === Number(spIdToUse));

  const spDepartment =
    departments.find(({ id }) => serviceProvider?.departmentId === id)?.name || null;
  const isQueueEnabled = resourceCentre?.settings?.queueSettings?.enableQueueSystem;
  const defaultOpdFee = resourceCentre?.settings?.opdSettings?.defaultOpdFee ?? "";
  const defaultAdvice = resourceCentre?.settings?.opdSettings?.defaultAdvice ?? "";

  return (
    <>
      <ReactToPrint
        trigger={() => trigger ?? <Button variant="contained">Print</Button>}
        content={() => printRef.current}
        onBeforePrint={onPrintClick}
        onAfterPrint={() => {
          // refetch the queue number after print
          // this is because the queue number is incremented on print
          if (isQueueEnabled) {
            refetch();
          }
        }}
      />

      <Box component="iframe" className="displayContents" title="printIframe">
        <Box
          ref={printRef}
          sx={{
            height: "calc(1.75in - 4.5mm)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between"
          }}
        >
          <Box sx={{ display: "flex", height: "100%" }}>
            <Box
              sx={{
                height: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.75
              }}
            >
              {/* Client */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <PrintRow
                  label="Patient Name"
                  value={`${fullName} ${
                    client?.dob ? `, ${getClientAge(client.dob, showClientAgeInYearsOnly)}` : ""
                  } ${client?.gender ? `/${getClientGender(client.gender)}` : ""}`}
                  emphasizeValue
                />

                {client?.customerNumber && (
                  <PrintRow
                    label="Patient/Regd. No"
                    value={getCustomerNumber(client.customerNumber)}
                  />
                )}

                {(client?.address || client?.city) && (
                  <PrintRow label="Address" value={separateByComma(client.address, client.city)} />
                )}

                {client?.phone && <PrintRow label="Phone" value={client.phone || "-"} />}
                <PrintRow label="Visit Date" value={`${BSDate} ${currentTime}`} />
              </Box>

              {/* Service Provider */}
              {serviceProvider && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <PrintRow
                    value={getSpFullName(serviceProvider).toUpperCase()}
                    label="Doctor Name"
                    emphasizeValue
                  />
                  <PrintRow
                    label="Qualification"
                    value={`${serviceProvider?.qualification} ${
                      serviceProvider?.speciality ? `, ${serviceProvider.speciality}` : ""
                    }`}
                  />
                  {serviceProvider?.registrationNumber && (
                    <PrintRow label="Reg. No" value={serviceProvider.registrationNumber} />
                  )}
                  {serviceProvider?.departmentId && spDepartment && (
                    <PrintRow label="Department" value={spDepartment} emphasizeValue />
                  )}
                </Box>
              )}

              <Box>
                {/* opd fee */}
                {defaultOpdFee && (
                  <PrintRow value={rupeesDisplayFull(defaultOpdFee)} label="OPD Fee" />
                )}

                {/* default advice */}
                {defaultAdvice && <PrintRow value={defaultAdvice} label="Advice" />}
              </Box>

              {/* rc info */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "end"
                }}
              >
                {/* rc name and address */}
                <Typography
                  sx={{
                    fontSize: "1.8mm",
                    fontFamily: "Verdana"
                  }}
                >
                  {separateByComma(resourceCentre?.name || "", resourceCentre?.address || "")}
                </Typography>
              </Box>
            </Box>

            {/* Logo + QR */}
            <Box
              sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}
            >
              {rcLogo && <img src={rcLogo} alt="logo" style={{ height: "1cm", width: "1cm" }} />}
              <QRCodeSVG size={43} value={getQRUrl(String(client.id) || "")} />

              {isQueueEnabled && (
                <Box sx={{ marginTop: 1, display: "flex", gap: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: "1.75mm",
                      fontFamily: "Verdana",
                      width: "15mm",
                      textAlign: "right"
                    }}
                  >
                    Queue No.:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.75mm",
                      fontFamily: "Verdana",
                      fontWeight: 700,
                      textAlign: "right"
                    }}
                  >
                    {typeof queueNumber === "number"
                      ? queueNumber.toString().padStart(5, "0")
                      : queueNumber || ""}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default connect(
  (state: RootState) => ({
    resourceCentre: state?.userContext?.resourceCentre,
    rcLogo:
      state.resources.resourceCentres.length &&
      state.resources.resourceCentres[0].resourceCentreLogo?.s3ResourceURL
  }),
  null
)(ClientStickerDefaultTemplate);
